/* eslint-disable no-mixed-spaces-and-tabs */
import React, { useEffect, useMemo, useState } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import {
	Box,
	Button,
	CircularProgress,
	Collapse,
	Skeleton,
	Stack,
	Typography,
	Grid,
} from '@mui/material';
import {
	useDeleteEmailTemplateMutation,
	useGetEmailTemplateHtmlQuery,
	useGetEmailTemplatesQuery,
	useGetHandlebarsHtmlQuery,
	useGetParsedEmailTemplateQuery,
	useSaveEmailTemplatePropertyMutation,
} from '@/features/templates/templatesApi';
import HtmlPreview from '@/components/HtmlPreview';
import testDealJson from '@/assets/json/test-deal.json';
import testClientJson from '@/assets/json/test-client.json';
import { useDispatch, useSelector } from 'react-redux';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import { FUSE_OPTIONS, TEMPLATE_GROUPS } from '@/features/templates/helper';
import { useFeatureFlagContext } from '@/context/featureFlagContext';
import { Add } from '@mui/icons-material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { openDialog } from '@/features/dialog/dialogSlice';
import { formatDate } from '@/utils/constants';
import { useLocation, useNavigate } from 'react-router-dom';
import DevJSONView from '@/components/DevJSONView';
import { ConfirmDialog } from '@/components/dialogs/ConfirmDialog';
import { showToast } from '@/features/toast/toastSlice';
import { setTemplate } from '@/features/templates/templatesSlice';
import { paths } from '@/app/routes';
import ShareDialog from '@/components/dialogs/ShareDialog';
import { NoRowsErrorOverlay, NoRowsOverlay } from '../../table/DataGridComponents/NoRowsOverlay';
import Fuse from 'fuse.js';
import SearchField from '@/components/SearchField';
import { stableSort } from '@/components/table/functions';
import { chunk } from 'lodash';
import { OwnerCellData } from '../OwnerCell';

const EmailTemplatesTable = () => {
	const dispatch = useDispatch();
	const [rowSelectionModel, setRowSelectionModel] = useState([]);
	const [selectedTemplate, setSelectedTemplate] = useState(null);

	const [searchValue, setSearchValue] = useState('');

	const [showDeleteDialog, setShowDeleteDialog] = useState(false);

	const showShareDialog = useSelector((state) => state.dialog.open?.share);

	const location = useLocation();
	const navigate = useNavigate();

	const isEditable = (row) =>
		row?.sortBy === TEMPLATE_GROUPS.user || row?.sortBy === TEMPLATE_GROUPS.generated;

	const emailTemplatesQuery = useGetEmailTemplatesQuery();

	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username;

	const { data: userDetails } = useGetUserDetailsQuery(
		{ email },
		{
			skip: !email,
		},
	);

	const { getFeatureFlags } = useFeatureFlagContext();
	const marbleLedger = Boolean(getFeatureFlags('marbleSOA')?.active);

	const client = {
		...(testClientJson?.clientRow ?? {}),
		brokerGroupId: userDetails?.brokerGroupId,
		ownerDocId: userDetails?.userId,
	};

	const deal = {
		...(testDealJson ?? {}),
		brokerGroupId: userDetails?.brokerGroupId,
		ownerDocId: userDetails?.userId,
	};

	const { data: templateData, ...templateQuery } = useGetParsedEmailTemplateQuery(
		{
			template: selectedTemplate,
			clientId: client?.clientId ?? deal?.clientDocId,
			dealId: deal?.dealId,
			isMarble: marbleLedger,
		},
		{
			skip:
				!selectedTemplate?.type ||
				!client?.clientId ||
				!deal?.clientDocId ||
				!deal?.dealId,
		},
	);

	const htmlQuery = useGetEmailTemplateHtmlQuery(
		{ id: selectedTemplate?.id, path: selectedTemplate?.path },
		{ skip: !selectedTemplate?.id || !selectedTemplate?.path },
	);

	const handlebarsQuery = useGetHandlebarsHtmlQuery(
		{ templateData, html: htmlQuery?.data?.toString() },
		{ skip: Object.keys(templateData ?? {}).length === 0 || !htmlQuery.data },
	);

	const templates = useMemo(() => {
		return (emailTemplatesQuery?.data ?? [])
			.filter((row) => !row?.aiGenerated)
			.map((row) => {
				return {
					...row,
					error:
						selectedTemplate?.id === row?.id &&
						(htmlQuery?.isError || handlebarsQuery?.isError),
				};
			});
	}, [emailTemplatesQuery?.data, htmlQuery?.isError, handlebarsQuery?.isError]);

	const searchFuse = useMemo(() => {
		const fuseIndex = Fuse.createIndex(FUSE_OPTIONS.keys, templates);
		return new Fuse(templates, FUSE_OPTIONS, fuseIndex);
	}, [templates]);

	const searchedPages = useMemo(() => {
		return searchFuse.search(searchValue).flatMap((v) => v?.item);
	}, [searchFuse, searchValue]);

	const rows = (searchValue ? searchedPages : templates) ?? [];

	const pageChunks = chunk(rows, 100);

	const [deleteEmailTemplate, { isLoading: isDeleteLoading }] = useDeleteEmailTemplateMutation();
	const [updateEmailTemplate, { isLoading: isUpdateLoading }] =
		useSaveEmailTemplatePropertyMutation();

	const handleUpdateEmailTemplate = async (oldRow, newRow) => {
		const templateName = newRow?.name;
		console.log('Updating email template', newRow);
		return await updateEmailTemplate({
			docId: selectedTemplate?.id,
			properties: { name: newRow?.name },
		})
			.unwrap()
			.then((res) => {
				console.log('updateEmailTemplate res', res);
				dispatch(
					showToast({
						message: `Successfully updated template name to "${templateName}".`,
						success: true,
					}),
				);
				return newRow;
			})
			.catch((e) => {
				console.error(`Error updating template name ${oldRow?.id}`, e);
				dispatch(
					showToast({
						message: `Error updating template name to "${templateName}"; please try again.`,
						error: true,
					}),
				);
				return oldRow;
			});
	};

	const handleDeleteEmailTemplate = async () => {
		const templateName = selectedTemplate?.name;
		console.log('Deleting email template', { selectedTemplate });
		await deleteEmailTemplate({ docId: selectedTemplate?.id })
			.unwrap()
			.then((res) => {
				console.log('deleteEmailTemplate res', res);
				dispatch(
					showToast({
						message: `Successfully deleted template "${templateName}".`,
						success: true,
					}),
				);
				setRowSelectionModel([]);
				setSelectedTemplate(null);
			})
			.catch((e) => {
				console.error(`Error deleting template ${selectedTemplate?.id}`, e);
				dispatch(
					showToast({
						message: `Error deleting template "${templateName}"; please try again.`,
						error: true,
					}),
				);
			});

		setShowDeleteDialog(false);
	};

	const isLoading =
		templateQuery.isFetching ||
		htmlQuery.isFetching ||
		templateQuery.isLoading ||
		htmlQuery.isLoading ||
		handlebarsQuery.isFetching ||
		handlebarsQuery.isLoading ||
		isDeleteLoading ||
		isUpdateLoading;

	const formattedHtml = isLoading || rowSelectionModel.length == 0 ? '' : handlebarsQuery.data;

	const columns = [
		{
			field: 'name',
			headerName: 'Template',
			flex: 2,
			minWidth: 250,
			editable: true,
			sortable: false,
			display: 'flex',
			renderCell: (params) => {
				const { name, error, aiGenerated } = params.row;
				return (
					<Stack
						direction='row'
						justifyContent='space-between'
						alignItems='start'
						width='100%'
						spacing={1}
					>
						<Box sx={{ flexGrow: 1, minWidth: 0 }}>
							<Typography {...(error && { sx: { color: '#d32f2f' } })}>
								{name}
							</Typography>
						</Box>

						{aiGenerated && (
							<AutoAwesomeIcon
								fontSize='small'
								sx={(theme) => ({ color: theme.palette.primary.main })}
							/>
						)}
					</Stack>
				);
			},
		},
		{
			field: 'ownerDocId',
			headerName: 'Created by',
			flex: 1,
			minWidth: 200,
			editable: false,
			sortable: false,
			display: 'flex',
			renderCell: (params) => {
				const { ownerDocId } = params.row;
				return ownerDocId ? (
					<OwnerCellData userId={ownerDocId} {...params} color='text.primary' />
				) : (
					<Typography color='text.primary'>{'Folio'}</Typography>
				);
			},
		},
		{
			field: 'createdWhen',
			headerName: 'Date created',
			flex: 1,
			minWidth: 200,
			editable: false,
			sortable: false,
			display: 'flex',
			renderCell: (params) => {
				const { createdWhen } = params.row;
				return <Typography>{formatDate(createdWhen, true)}</Typography>;
			},
		},
		{
			field: 'lastUpdated',
			headerName: 'Last updated',
			flex: 1,
			minWidth: 200,
			editable: false,
			sortable: false,
			display: 'flex',
			renderCell: (params) => {
				const { lastUpdated } = params.row;
				return <Typography>{formatDate(lastUpdated, true)}</Typography>;
			},
		},
	];

	const visible = Boolean(selectedTemplate?.id);
	const actionButtonsDisabled =
		selectedTemplate?.error ||
		handlebarsQuery.isFetching ||
		!handlebarsQuery.isSuccess ||
		!htmlQuery.isSuccess;

	return (
		<Box sx={{ minHeight: '70vh' }}>
			<Stack
				direction='column'
				spacing={1}
				sx={{ height: '100%' }}
				onClick={() => {
					if (!showDeleteDialog && !showShareDialog) {
						setRowSelectionModel([]);
						setSelectedTemplate(null);
					}
				}}
			>
				<Grid container>
					<Grid size={{ xs: 12, sm: 4 }}>
						<Box>
							<SearchField
								value={searchValue}
								onChange={(event) => setSearchValue(event.target.value)}
								onReset={() => setSearchValue('')}
								helperText={''}
							/>
						</Box>
					</Grid>
					<Grid size={{ xs: 12, sm: 3 }} container>
						<Grid
							size={12}
							spacing={1}
							sx={{ px: '1em' }}
							container
							justifyContent={'flex-end'}
							alignItems={'baseline'}
						>
							{rowSelectionModel.length > 0 && (
								<>
									<Box>
										<Button
											variant='table_actions'
											disabled={actionButtonsDisabled}
											onClick={(e) => {
												e.stopPropagation();
												dispatch(setTemplate(selectedTemplate?.id));
												navigate(`${paths.templates}/create`);
											}}
										>
											Edit
										</Button>
									</Box>
									<Box>
										<Button
											variant='table_actions'
											disabled={
												actionButtonsDisabled ||
												!isEditable(selectedTemplate)
											}
											onClick={(e) => {
												e.stopPropagation();
												console.log(
													'Current template to share',
													selectedTemplate,
												);
												dispatch(
													openDialog({
														dialogName: 'share',
														data: { ...selectedTemplate },
													}),
												);
											}}
										>
											Share
										</Button>
									</Box>
									<Box>
										<Button
											variant='table_actions'
											disabled={
												handlebarsQuery.isFetching ||
												!isEditable(selectedTemplate)
											}
											onClick={(e) => {
												e.stopPropagation();
												setShowDeleteDialog(true);
											}}
										>
											Delete
										</Button>
									</Box>
								</>
							)}

							<Box>
								<Button
									variant='table_actions'
									disabled={actionButtonsDisabled}
									startIcon={<Add />}
									onClick={(e) => {
										e.stopPropagation();
										dispatch(
											openDialog({
												dialogName: 'email',
												...(selectedTemplate?.id && {
													data: { templateId: selectedTemplate.id },
												}),
											}),
										);
									}}
								>
									{`Send email${selectedTemplate?.id ? ' with template' : ''}`}
								</Button>
							</Box>
						</Grid>
					</Grid>
				</Grid>

				<Box
					sx={{
						display: { xs: 'block', sm: 'flex' },
						alignItems: 'stretch',
					}}
				>
					<Box
						sx={(theme) => ({
							width: { xs: '100%', sm: visible ? '50%' : '100%' },
							transition: theme.transitions.create('width', {
								duration: theme.transitions.duration.standard,
								easing: theme.transitions.easing.easeInOut,
							}),
							minWidth: 0,
						})}
					>
						<DataGrid
							sx={{
								'& .MuiDataGrid-editInputCell': {
									backgroundColor: 'transparent',
								},
								'--DataGrid-overlayHeight': '3em',
								// '--DataGrid-containerBackground': 'transparent',
								// '--DataGrid-pinnedBackground': 'transparent',
								'& .MuiDataGrid-cell': {
									padding: 1,
									minHeight: '42px',
								},
								border: 'none',
							}}
							loading={
								emailTemplatesQuery.isLoading || isDeleteLoading || isUpdateLoading
							}
							// autoHeight
							width='100%'
							getRowHeight={() => 'auto'}
							processRowUpdate={async (newRow, oldRow) => {
								console.log('processRowUpdate', { newRow, oldRow });
								return await handleUpdateEmailTemplate(oldRow, newRow);
							}}
							rows={rows}
							columns={columns}
							isCellEditable={(params) =>
								params.field === 'name' && isEditable(params.row)
							}
							// isRowSelectable={() => false}
							// getRowId={(row) => `${row.insurer}`}
							onRowSelectionModelChange={(model) => {
								const rowId = model?.[0];
								setRowSelectionModel(model);
								setSelectedTemplate(rows.find((r) => r.id === rowId));
							}}
							rowSelectionModel={rowSelectionModel}
							disableColumnResize
							// disableRowSelectionOnClick
							// disableSelectionOnClick
							disableColumnFilter
							disableColumnSelector
							disableColumnMenu
							onRowClick={(_, e) => {
								e.stopPropagation();
							}}
							hideFooter
							slots={{
								noRowsOverlay: emailTemplatesQuery.isError
									? () => <NoRowsErrorOverlay objectType={'templates'} />
									: () => <NoRowsOverlay objectType={'templates'} />,
							}}
						/>
					</Box>
					<Box
						sx={(theme) => ({
							width: { xs: '100%', sm: visible ? '50%' : 0 },
							transition: theme.transitions.create('width', {
								duration: theme.transitions.duration.standard,
								easing: theme.transitions.easing.easeInOut,
							}),
							overflow: 'hidden',
							minWidth: 0,
							px: '1em',
						})}
					>
						<>
							{isLoading ? (
								<Box
									width='100%'
									display='flex'
									sx={{ justifyContent: 'center', mt: '1em', pt: '1em' }}
								>
									<CircularProgress />
								</Box>
							) : rowSelectionModel.length === 0 ? (
								<Box
									height='100%'
									width='100%'
									sx={{ backgroundColor: 'aliceblue' }}
								></Box>
							) : handlebarsQuery.isError || htmlQuery.isError ? (
								<Box width='100%' sx={{ mt: '1em' }}>
									<Typography sx={{ color: '#d32f2f' }}>
										{`Error loading template${
											htmlQuery.isError &&
											htmlQuery.error?.message?.includes('object-not-found')
												? ': File not found.'
												: '.'
										}`}
									</Typography>
								</Box>
							) : (
								<HtmlPreview showLabel={false} htmlValue={formattedHtml} />
							)}
						</>
					</Box>
				</Box>
				<DevJSONView value={{ selectedTemplate, rowSelectionModel }} />
				{showDeleteDialog && (
					<ConfirmDialog
						openDialog={showDeleteDialog}
						title={'Delete Email Template'}
						handleClose={(e) => {
							e.stopPropagation();
							setShowDeleteDialog(false);
						}}
						handleConfirm={handleDeleteEmailTemplate}
						content={
							<Stack spacing={1}>
								<Typography>
									{
										'Are you sure you want to delete this email template? This cannot be undone.'
									}
								</Typography>
								<Typography>
									{
										'If you have shared this template with others, they will no longer be able to access it.'
									}
								</Typography>
							</Stack>
						}
					/>
				)}
				{showShareDialog && (
					<ShareDialog
						title={'Template'}
						showAdmin={userDetails?.isFolioAdmin}
						onConfirm={async (userIds, brokerGroupIds) => {
							if (selectedTemplate) {
								const newUserIds = Array.from(
									new Set([...userIds, userDetails?.userId]),
								);

								await updateEmailTemplate({
									docId: selectedTemplate?.id,
									properties: { userIds: newUserIds, brokerGroupIds },
								})
									.unwrap()
									.then((res) => {
										console.log('updateEmailTemplate res', res);
										dispatch(
											showToast({
												message:
													'Successfully updated template sharing settings.',
												success: true,
											}),
										);
									})
									.catch((e) => {
										console.error('Error sharing template', e);
										dispatch(
											showToast({
												message:
													'Error sharing template. Please try again.',
												error: true,
											}),
										);
									});
							}
						}}
					/>
				)}
			</Stack>
		</Box>
	);
};

export default EmailTemplatesTable;
